/**
 * Lookup Control Component
 * Renders lookup/reference to another collection
 */

import React, { useState, useEffect } from 'react';
import { Box, TextField, CircularProgress, Chip, Autocomplete } from '@mui/material';
import { ControlComponentProps } from '../../../../types/ControlTypes';
import { LookupSettings } from '../../../../types/ControlTypes';
import { LabelWithInfo } from './LabelWithInfo';

interface LookupOption {
  label: string;
  value: any;
}

export const LookupControl: React.FC<ControlComponentProps> = ({
  name,
  value,
  onChange,
  error,
  disabled,
  readonly,
  label,
  description,
  required,
  schema,
  control,
}) => {
  const settings = control.settings as LookupSettings;
  const [options, setOptions] = useState<LookupOption[]>([]);
  const [loading, setLoading] = useState(false);

  // Lookup config comes entirely from x-control.settings
  const source = settings?.source;
  const keyProp = settings?.keyProp || 'id';
  const renderProp = settings?.renderProp || 'name';
  const multiselect = settings?.multiselect || false;
  const placeholder = settings?.placeholder || 'Select...';
  const baseFilter = settings?.baseFilter;

  // Fetch options from API
  useEffect(() => {
    if (!source) return;

    const fetchOptions = async () => {
      try {
        setLoading(true);
        const body: Record<string, any> = {
          q: '',
          page: 1,
          limit: 100,
          sortBy: 'createdAt',
          sortOrder: 'desc',
        };
        if (baseFilter) {
          body.filterGroup = baseFilter;
        }
        const response = await fetch(
          `/api/globals/collection/${source}/search`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          }
        );
        const data = await response.json();
        
        // Response format: { statusCode, data: { items: [...], pagination: {...} } }
        if (data.statusCode === 200 && data.data?.items && Array.isArray(data.data.items)) {
          const mapped = data.data.items.map((item: any) => {
            const itemData = item.data || {};
            return {
              label: itemData[renderProp] || itemData.name || item.id,
              value: item.id,  // always use item.id as the key
            };
          });
          setOptions(mapped);
        }
      } catch (err) {
        console.error('Failed to fetch lookup options:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchOptions();
  }, [source, keyProp, renderProp, baseFilter]);

  const selectedValues = multiselect
    ? Array.isArray(value) ? value : []
    : value ? [value] : [];

  const selectedOptions = options.filter(opt =>
    selectedValues.includes(opt.value)
  );

  if (multiselect) {
    return (
      <Box sx={{ mb: 2 }}>
        <Box sx={{ mb: 1 }}>
          <LabelWithInfo
            label={label || name}
            description={description}
            required={required}
          />
        </Box>

        <Autocomplete
          multiple
          options={options}
          getOptionLabel={(option) => option.label}
          value={selectedOptions}
          onChange={(e, newValue) => {
            onChange(newValue.map(v => v.value));
          }}
          disabled={disabled || readonly || loading}
          loading={loading}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              error={!!error}
              helperText={error}
              placeholder={placeholder}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Box>
    );
  }

  // Single select
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ mb: 1 }}>
        <LabelWithInfo
          label={label || name}
          description={description}
          required={required}
        />
      </Box>

      <Autocomplete
        options={options}
        getOptionLabel={(option) => option.label}
        value={selectedOptions[0] || null}
        onChange={(e, newValue) => {
          onChange(newValue ? newValue.value : null);
        }}
        disabled={disabled || readonly || loading}
        loading={loading}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            error={!!error}
            helperText={error}
            placeholder={placeholder}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
};

export default LookupControl;
